import React, { useEffect, useState } from "react";
import { useScannerContext } from "../scanners/ScannerContext";
import { BarcodeDataType, OrderCheckCode, CounterPickupApiResponse } from "../scanners/types";
import { apiHost, apiKey, showManualQrCodeInput } from "@/constants";
import { appLog, barcodeDataTypeFromQrCode, hasQRcodeNumberPattern } from "../scanners/helpers";
import { BagCard } from "@/components/ScannerActions/BagCard";
import QrCodeInputField from "../scanners/QrCodeInputField";
import { OrderBag } from "@/types";

const messages = {
  scan_bag_qrcode_message: "Scan een zak barcode",
  invalid_qrcode_format_message: "Geen geldig formaat voor QR code van zak",
  invalid_qrcode_format_log: "[SCANNER_DATA] not a QRcode Number Pattern",
};

export const Scan: React.FC = () => {
  const { scannedData, setScannedData, enableScanner, clearScannedData } = useScannerContext();
  const [bagDataString, setBagDataString] = useState<string | null>(null);
  const [orderBag, setOrderBag] = useState<Partial<OrderBag> | null>(null);
  const [qualityCheckResponse, setQualityCheckResponse] = useState<CounterPickupApiResponse | null>(null);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    return () => {
      clearScannedData();
    };
  }, [clearScannedData]);

  useEffect(() => {
    if (scannedData) {
      if (hasQRcodeNumberPattern(scannedData)) {
        // Get the entity the barcode belongs to (product, bag, bin)
        const dataLabelType = barcodeDataTypeFromQrCode(scannedData);
        // Bag scan
        if (dataLabelType === BarcodeDataType.ORDER_BAG) {
          setBagDataString(scannedData);
        } else {
          setMessage(messages.invalid_qrcode_format_message);
          appLog(`[KWC-BAGS] ${messages.invalid_qrcode_format_log}`, true, "info", { datalabel: scannedData });
        }
      } else {
        setMessage(messages.invalid_qrcode_format_message);
        appLog(`[KWC-BAGS] ${messages.invalid_qrcode_format_log}`, true, "info", { datalabel: scannedData });
      }
    }
  }, [scannedData, bagDataString]);

  const fetchApiData = async (bagDataString: string | null) => {
    try {
      if (!bagDataString) {
        return;
      }
      // Add qr-code dataStrings to the request body
      const data =  {
        dataString: bagDataString,
        type: BarcodeDataType.ORDER_BAG
      };

      const response = await fetch(`${apiHost}/orders/counter-pickup-by-datastring`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
        },
        body: JSON.stringify(data),
      });

      const responseData: CounterPickupApiResponse = await response.json();
      // invalid response
      if (responseData?.message) {
        responseData.code = responseData.message as OrderCheckCode;
      }
      // valid response
      if (typeof responseData?.counterPickupAt === "string" && responseData?.counterPickupAt.length > 0) {
        responseData.orderBag = responseData as OrderBag;
      }
      setQualityCheckResponse(responseData || null);
    } catch (err) {
      appLog((err as Error).message, true, "error", err as Error);
      setOrderBag(null);
    }
  };

  useEffect(() => {
    setMessage("");
    if (bagDataString) {
      fetchApiData(bagDataString);
    }
  }, [bagDataString]);

  useEffect(() => {
    enableScanner(true);
  }, [enableScanner]);

  return (
    <>
      <BagCard data={qualityCheckResponse} orderBag={orderBag} showScanMessage={true} />
      {showManualQrCodeInput && (
        <QrCodeInputField
          onChange={(value: string) => {
            setScannedData(value);
          }}
        />
      )}
      {message && <div>{message}</div>}
    </>
  );
};

export default Scan;
